import { ConflictException, Injectable, NotFoundException } from '@nestjs/common';
import { Application } from '@chat/prisma-client';
import { PrismaService } from '../prisma/prisma.service';
import { generateApiKey } from '../common/crypto/api-key.util';
import { CreateApplicationDto } from './dto/create-application.dto';
import { IssueApiKeyDto } from './dto/issue-api-key.dto';

@Injectable()
export class ApplicationsService {
  constructor(private readonly prisma: PrismaService) {}

  async create(dto: CreateApplicationDto): Promise<Application> {
    const existing = await this.prisma.application.findUnique({ where: { slug: dto.slug } });
    if (existing) {
      throw new ConflictException(`An application with slug "${dto.slug}" already exists`);
    }

    return this.prisma.application.create({
      data: {
        slug: dto.slug,
        name: dto.name,
        allowedOrigins: dto.allowedOrigins ?? [],
      },
    });
  }

  async list(): Promise<Application[]> {
    return this.prisma.application.findMany({
      where: { deletedAt: null },
      orderBy: { createdOn: 'asc' },
    });
  }

  // Returns the raw key exactly once — only the hash is ever persisted, so
  // this is the one and only chance the caller has to see it.
  async issueApiKey(applicationId: string, dto: IssueApiKeyDto): Promise<{ id: string; label: string; rawKey: string }> {
    const application = await this.prisma.application.findUnique({ where: { id: applicationId } });
    if (!application || application.deletedAt) {
      throw new NotFoundException('Application not found');
    }

    const { rawKey, keyHash } = generateApiKey();
    const apiKey = await this.prisma.apiKey.create({
      data: { applicationId, keyHash, label: dto.label },
    });

    return { id: apiKey.id, label: apiKey.label, rawKey };
  }

  async revokeApiKey(applicationId: string, apiKeyId: string): Promise<void> {
    const apiKey = await this.prisma.apiKey.findUnique({ where: { id: apiKeyId } });
    if (!apiKey || apiKey.applicationId !== applicationId) {
      throw new NotFoundException('API key not found for this application');
    }

    await this.prisma.apiKey.update({
      where: { id: apiKeyId },
      data: { revokedAt: new Date() },
    });
  }
}
